import { formatNumberToCurrency } from '@/utils/currency';
import { Box, DialogContent, Typography } from '@mui/material';
import { t } from 'i18next';
import AddAccountProgressBar from '../shared/AddAccountProgressBar';
import ModalFooter from '../shared/ModalFooter';
import ModalSectionTitle from '../shared/ModalSectionTitle';
import { StepsForManual } from '../useAddAccountNavigation';
import HistoricalValuesForm from './ManualHistoricalValuesForm';
import { AccountDetailsFormState } from '../ManualAccountDetails/useAccountDetailsForm';
import { PortfolioDiversificationFormState } from '../ManualPortfolioDiversification/usePortfolioDiversificationForm';
import { HistoricalValuesFormState } from './useHistoricalValuesForm';
import { RealEstateDetailsFormState } from '../ManualRealEstateDetails/useRealEstateDetailsForm';
import { useMemo } from 'react';
import CategoryUtility, { ClassificationItem } from '../categories.utility';

interface ManualHistoricalValuesProps {
  handleOnCancel: () => void;
  footerLabels: {
    save: string;
    back: string;
    continue: string;
    cancel: string;
  };
  selectedClassification: ClassificationItem;
  accountDetailsForm: AccountDetailsFormState;
  realEstateDetailsForm: RealEstateDetailsFormState;
  updateModalStep: (step: StepsForManual) => void;
  portfolioDiversificationForm: PortfolioDiversificationFormState;
  historicalValuesForm: HistoricalValuesFormState;
  updateHistoricalValuesForm: (state: HistoricalValuesFormState) => void;
  handleSave: () => Promise<boolean>;
  isSaving: boolean;
}

const ManualHistoricalValues = ({
  handleOnCancel,
  footerLabels,
  selectedClassification,
  updateModalStep,
  accountDetailsForm,
  realEstateDetailsForm,
  historicalValuesForm,
  updateHistoricalValuesForm,
  handleSave,
  isSaving,
}: ManualHistoricalValuesProps) => {
  const currentValue = useMemo(() => {
    if (CategoryUtility.isRealEstate(selectedClassification.ClassificationId)) {
      return realEstateDetailsForm.values.PropertyValue || '';
    }

    return accountDetailsForm.values.AccountBalance || '';
  }, [
    accountDetailsForm.values.AccountBalance,
    realEstateDetailsForm.values.PropertyValue,
    selectedClassification.ClassificationId,
  ]);

  return (
    <>
      <DialogContent>
        <Box sx={{ marginBottom: 4 }}>
          <AddAccountProgressBar
            activeStep={StepsForManual.MANUAL_HISTORICAL_VALUES}
          />
          <ModalSectionTitle>
            {t('addAccountModal.historicalValues.title')}
          </ModalSectionTitle>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {selectedClassification.Name}
          </Typography>
        </Box>
        <Box mb={4}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {t('addAccountModal.historicalValues.currentValue')}:{' '}
            {formatNumberToCurrency(Number(currentValue))}
          </Typography>
        </Box>

        <HistoricalValuesForm
          initialValues={historicalValuesForm.values}
          onFormChange={updateHistoricalValuesForm}
          currentValue={currentValue}
        />
      </DialogContent>

      <ModalFooter
        onCancel={handleOnCancel}
        onBack={() => {
          updateModalStep(StepsForManual.MANUAL_PORTFOLIO_DIVERSIFICATION);
        }}
        label={{ ...footerLabels, primaryCta: footerLabels.save }}
        onPrimaryAction={() => {
          updateModalStep(StepsForManual.MANUAL_ACCOUNT_ADDED);
          void handleSave();
        }}
        isPrimaryActionDisabled={!historicalValuesForm.isValid || isSaving}
        isPrimaryActionLoading={isSaving}
      />
    </>
  );
};

export default ManualHistoricalValues;
